/**
 * Pictures for spells and prayers, drawn as lines on a 24 by 24 square.
 *
 * A library rather than a picture per spell: somebody writing a spell in the
 * tools picks one of these and a colour, and a spell book of forty spells in
 * forty colours reads at a glance without anybody drawing forty pictures.
 */

export const GLYPHS: Readonly<Record<string, JSX.Element>> = {
  orb: <circle cx="12" cy="12" r="7" />,
  wind: (
    <>
      <path d="M3 9h11a3 3 0 1 0-3-3" />
      <path d="M3 15h15a3 3 0 1 1-3 3" />
      <path d="M3 12h7" />
    </>
  ),
  drop: <path d="M12 3c4.5 5.5 7 8.8 7 11.6a7 7 0 0 1-14 0C5 11.8 7.5 8.5 12 3z" />,
  stone: <path d="M6 18 3.5 11 8 5l8 1 4.5 6-3 6.5z" />,
  flame: (
    <path d="M12 21c-4 0-6-3-6-6 0-4 3-6 4-10 2 2 3 4 3 6 1-1 2-2 2-4 2 2 3 5 3 8 0 3-2 6-6 6z" />
  ),
  burst: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v5M12 17v5M2 12h5M17 12h5M5 5l3.5 3.5M15.5 15.5 19 19M5 19l3.5-3.5M15.5 8.5 19 5" />
    </>
  ),
  bolt: <path d="M13 2 5 13h6l-1 9 8-11h-6z" />,
  chain: (
    <>
      <path d="M9.5 14.5 7 17a3.5 3.5 0 0 1-5-5l3-3a3.5 3.5 0 0 1 5 0" />
      <path d="M14.5 9.5 17 7a3.5 3.5 0 0 1 5 5l-3 3a3.5 3.5 0 0 1-5 0" />
      <path d="m9 15 6-6" />
    </>
  ),
  heart: <path d="M12 20s-7-4.4-7-9.5A4 4 0 0 1 12 8a4 4 0 0 1 7 2.5C19 15.6 12 20 12 20z" />,
  portal: (
    <>
      <ellipse cx="12" cy="12" rx="6" ry="9" />
      <ellipse cx="12" cy="12" rx="2.5" ry="5" />
    </>
  ),
  coin: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v10M9.5 9.5h4a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3h4" />
    </>
  ),
  sword: (
    <>
      <path d="M19 5 9.8 14.2" />
      <path d="M19 5h-3.5M19 5v3.5" />
      <path d="M7.2 11.6l5.2 5.2" />
      <path d="M9.8 14.2 5.2 18.8" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" />,
  ward: (
    <>
      <path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 3c.8 4.2 2.8 6.2 7 7-4.2.8-6.2 2.8-7 7-.8-4.2-2.8-6.2-7-7 4.2-.8 6.2-2.8 7-7z" />
      <path d="M19 17v4M17 19h4" />
    </>
  ),
  leaf: (
    <>
      <path d="M5 19C5 10 10 5 20 4c0 10-5 15-15 15z" />
      <path d="M5 19 13 11" />
    </>
  ),
  skull: (
    <>
      <path d="M12 3a8 8 0 0 0-5 14.2V20h10v-2.8A8 8 0 0 0 12 3z" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
    </>
  ),
  hand: (
    <path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V11M11 11V4a1.5 1.5 0 0 1 3 0v7M14 11V5.5a1.5 1.5 0 0 1 3 0V14c0 4-2.5 7-6 7-2.5 0-4-1.5-5.5-4L4 13.5a1.5 1.5 0 0 1 2.5-1.5L8 14" />
  ),
  star: <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />,
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
    </>
  ),
  snowflake: (
    <>
      <path d="M12 2v20M3.3 7l17.4 10M3.3 17 20.7 7" />
      <path d="m9 4 3 2 3-2M9 20l3-2 3 2" />
    </>
  ),
  hourglass: (
    <>
      <path d="M6 3h12M6 21h12" />
      <path d="M7 3c0 5 10 5 10 9s-10 4-10 9M17 3c0 5-10 5-10 9s10 4 10 9" />
    </>
  ),
  arrow: (
    <>
      <path d="M4 20 18 6" />
      <path d="M13 5h6v6" />
      <path d="m4 20 1-4M4 20l4-1" />
    </>
  ),
  house: (
    <>
      <path d="M3 11 12 4l9 7" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-5h4v5" />
    </>
  ),
  feather: (
    <>
      <path d="M6 18C9 11 13 7 20 4c-1 7-5 11-13 13" />
      <path d="M4 20 13 11" />
    </>
  ),
  gem: (
    <>
      <path d="M6 4h12l3 5-9 11L3 9z" />
      <path d="M3 9h18" />
    </>
  ),
  paw: (
    <>
      <circle cx="12" cy="15.5" r="3.5" />
      <circle cx="6.5" cy="10" r="1.6" />
      <circle cx="10" cy="6.5" r="1.6" />
      <circle cx="14" cy="6.5" r="1.6" />
      <circle cx="17.5" cy="10" r="1.6" />
    </>
  ),
  cross: <path d="M12 3v18M5 9h14" />,
  book: (
    <>
      <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
      <path d="M4 19V5M8 7h7" />
    </>
  ),
  potion: (
    <>
      <path d="M9 3h6M10 3v5l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" />
      <path d="M7.5 15h9" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="14" r="4" />
      <path d="m11 11 9-9M17 5l2 2M15 7l2 2" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
};

/** Every glyph there is, in the order the tools offer them. */
export const GLYPH_NAMES: readonly string[] = Object.keys(GLYPHS);
